import config from '../config/environment';
import supabase from '../config/supabase';
import type { WorkoutImportRow } from '../utils/workoutHistoryImport';
import { handleServiceError } from '../utils/errorHandler';

const IMPORT_TRAININGS_FUNCTION_NAME = 'import-trainings';
const COMMIT_BATCH_SIZE = 100;
const ALREADY_IMPORTED_MESSAGE = 'Esses treinos já estão cadastrados.';

export interface TrainingImportCommitResult {
  importedCount: number;
}

interface MatchResponse {
  existingNames?: string[];
}

interface CommitResponse {
  importedCount?: number;
  error?: string;
}

class TrainingImportServiceClass {
  private async getFunctionHeaders(): Promise<Record<string, string>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      apikey: config.supabaseAnonKey,
    };
  }

  private async post<TResponse>(body: unknown, context: string): Promise<TResponse> {
    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${IMPORT_TRAININGS_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({})) as TResponse & { error?: string };

    if (!response.ok) {
      handleServiceError(new Error(payload?.error ?? `Erro ao ${context}`), context);
    }

    return payload;
  }

  async matchNames(names: readonly string[]): Promise<string[]> {
    if (names.length === 0) {
      return [];
    }

    const payload = await this.post<MatchResponse>({ action: 'match', names }, 'conferir treinos já cadastrados');
    return payload.existingNames ?? [];
  }

  async commit(rows: readonly WorkoutImportRow[]): Promise<TrainingImportCommitResult> {
    let importedCount = 0;

    for (let offset = 0; offset < rows.length; offset += COMMIT_BATCH_SIZE) {
      const trainings = rows.slice(offset, offset + COMMIT_BATCH_SIZE).map((row) => ({
        name: row.name,
        category: row.category,
        levels: row.levels,
        objective: row.objective,
        monthIndex: row.monthIndex,
        location: row.location,
        sessionsPerWeek: row.sessionsPerWeek,
        durationMinutes: row.durationMinutes,
      }));

      try {
        const payload = await this.post<CommitResponse>({ action: 'commit', trainings }, 'importar treinos');
        importedCount += payload.importedCount ?? 0;
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : '';
        if (message === ALREADY_IMPORTED_MESSAGE) {
          continue;
        }
        if (importedCount > 0) {
          throw new Error(`A importação parou depois de ${importedCount} treinos. ${message}`.trim());
        }
        throw caught;
      }
    }

    if (importedCount === 0) {
      throw new Error(ALREADY_IMPORTED_MESSAGE);
    }

    return { importedCount };
  }
}

export const TrainingImportService = new TrainingImportServiceClass();
